import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CalEvent, Holiday } from "../../api/types";
import { HOLIDAY_COLOR } from "../../lib/colors";
import { MONTHS_SHORT, WEEKDAYS_SHORT, addDays, datePart, diffDays, formatTime, longDateLabel, minutesOf, nowIn, parts, weekday, type DateStr } from "../../lib/date";
import { newDraft } from "../../lib/draft";
import { layoutBars } from "../../lib/layout";
import { anchorOf, useUi, type Anchor } from "../../state/ui";
import { Popover } from "../ui/Popover";
import { endDay, isBarEvent, isPast, useHolidayPopover, useMoveEvent, useVisibleEvents } from "./common";
import "./MonthView.css";

const ITEM_H = 22;
const HEAD_H = 26;

type Item = { key: string; event?: CalEvent; holiday?: Holiday };

export function MonthView() {
  const ui = useUi();
  const days = ui.visibleDays; // 5 or 6 whole weeks covering the month
  const month = parts(ui.date).m;
  const weeks = days.length / 7;
  const from = days[0];
  const to = addDays(days[days.length - 1], 1);
  const { events, holidays } = useVisibleEvents(from, to);
  const moveEvent = useMoveEvent();
  const holidayPopover = useHolidayPopover();
  const now = nowIn(ui.me.timezone);
  const gridRef = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState(120);
  const [more, setMore] = useState<{ day: DateStr; anchor: Anchor } | null>(null);
  /** `grabDay`: the day under the pointer when the drag began (a multi-day bar moves by the difference). */
  const [dragging, setDragging] = useState<{ key: string; grabDay: DateStr; overDay: DateStr | null } | null>(null);

  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const measure = () => setRowHeight(el.clientHeight / weeks);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [weeks]);

  const byKey = useMemo(() => new Map(events.map((e) => [e.key, e])), [events]);
  const items: Array<{ key: string; start: DateStr; endExclusive: DateStr; order: number }> = useMemo(
    () => [
      ...holidays.map((h) => ({ key: `holiday:${h.date}:${h.name}`, start: h.date, endExclusive: addDays(h.date, 1), order: 0 })),
      ...events.map((e) => ({
        key: e.key,
        start: datePart(e.start),
        endExclusive: isBarEvent(e) ? endDay(e) : addDays(datePart(e.start), 1),
        order: isBarEvent(e) ? 1 : 2 + minutesOf(e.start)
      }))
    ],
    [events, holidays]
  );
  const itemFor = (key: string): Item => {
    const event = byKey.get(key);
    return event ? { key, event } : { key, holiday: holidays.find((h) => `holiday:${h.date}:${h.name}` === key) };
  };

  const maxLevels = Math.max(1, Math.floor((rowHeight - HEAD_H - 4) / ITEM_H));

  const openItem = (item: Item, el: Element) => {
    if (item.event) ui.setOverlay({ kind: "details", event: item.event, anchor: anchorOf(el) });
    else if (item.holiday) holidayPopover.open(item.holiday, anchorOf(el));
  };

  const renderItem = (item: Item, style: React.CSSProperties, span = 1, className = "") => {
    const e = item.event;
    const bar = !e || isBarEvent(e);
    const color = e?.color ?? HOLIDAY_COLOR;
    const past = e ? isPast(e, now.date, now.minutes) : false;
    return (
      <button
        key={item.key}
        className={`mv-item${bar ? " bar ev-surface" : ""}${past ? " past" : ""}${dragging?.key === item.key ? " dragging" : ""}${className}`}
        style={{ ...style, ...(bar ? { ["--ev" as string]: color } : {}) }}
        draggable={Boolean(e)}
        onDragStart={(ev) => {
          if (!e) return;
          ev.dataTransfer.setData("text/plain", item.key);
          ev.dataTransfer.effectAllowed = "move";
          const week = ev.currentTarget.closest<HTMLElement>(".mv-week");
          setDragging({ key: item.key, grabDay: week ? dayAt(week, ev.clientX) : datePart(e.start), overDay: null });
        }}
        onDragEnd={() => setDragging(null)}
        onClick={(ev) => {
          ev.stopPropagation();
          openItem(item, ev.currentTarget);
        }}
        title={e ? e.title : item.holiday?.name}
        data-span={span}
      >
        {!bar && <span className="color-dot" style={{ background: color, width: 8, height: 8 }} />}
        {!bar && <span className="mv-time">{formatTime(minutesOf(e!.start))}</span>}
        <span className="mv-title">{e ? e.title || "(sem título)" : item.holiday?.name}</span>
      </button>
    );
  };

  /** Day of the week row under `clientX` (rows carry their first day in data-row-start). */
  const dayAt = (week: HTMLElement, clientX: number): DateStr => {
    const rect = week.getBoundingClientRect();
    const col = Math.max(0, Math.min(6, Math.floor(((clientX - rect.left) / rect.width) * 7)));
    return addDays(week.dataset.rowStart!, col);
  };

  const dropOn = (day: DateStr, key: string) => {
    const e = byKey.get(key);
    if (!e) return;
    const delta = diffDays(dragging?.key === key ? dragging.grabDay : datePart(e.start), day);
    if (delta) void moveEvent(e, { deltaDays: delta });
  };

  return (
    <div className="mv">
      <div className="mv-head">
        {days.slice(0, 7).map((d) => (
          <div key={d} className="mv-dow">
            {WEEKDAYS_SHORT[weekday(d)].toUpperCase()}
          </div>
        ))}
      </div>
      <div className="mv-grid" ref={gridRef} style={{ gridTemplateRows: `repeat(${weeks}, 1fr)` }}>
        {Array.from({ length: weeks }, (_, w) => {
          const rowStart = days[w * 7];
          const placements = layoutBars(items, rowStart, 7);
          const shown = placements.filter((p) => p.level < maxLevels - 1 || (p.level === maxLevels - 1 && placements.every((q) => q.level < maxLevels)));
          const hiddenPerDay = Array.from({ length: 7 }, (_, c) => placements.filter((p) => !shown.includes(p) && c >= p.col && c < p.col + p.span).length);
          return (
            <div
              key={rowStart}
              className="mv-week"
              data-row-start={rowStart}
              style={{ gridTemplateRows: `${HEAD_H}px repeat(${maxLevels}, ${ITEM_H}px) 1fr` }}
              // On the row, not the cells: events cover most of a cell and must not block the drop.
              onDragOver={(ev) => {
                if (!dragging) return;
                ev.preventDefault();
                const day = dayAt(ev.currentTarget, ev.clientX);
                if (dragging.overDay !== day) setDragging({ ...dragging, overDay: day });
              }}
              onDrop={(ev) => {
                ev.preventDefault();
                dropOn(dayAt(ev.currentTarget, ev.clientX), ev.dataTransfer.getData("text/plain"));
                setDragging(null);
              }}
            >
              {Array.from({ length: 7 }, (_, c) => {
                const day = days[w * 7 + c];
                const { d, m } = parts(day);
                return (
                  <div
                    key={day}
                    className={`mv-cell${m !== month ? " other" : ""}${dragging?.overDay === day ? " drop" : ""}`}
                    style={{ gridColumn: c + 1, gridRow: "1 / -1" }}
                    onClick={(ev) =>
                      !ui.overlay &&
                      ui.setOverlay({ kind: "quick", draft: newDraft({ date: day, allDay: true, settings: ui.me.settings, calendars: ui.calendars }), anchor: anchorOf(ev.currentTarget) })
                    }
                  >
                    <button
                      className={`mv-daynum${day === now.date ? " today" : ""}`}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        ui.setDate(day);
                        ui.setView("day");
                      }}
                      aria-label={longDateLabel(day)}
                    >
                      {d === 1 ? `${d} de ${MONTHS_SHORT[m - 1].replace(".", "")}` : d}
                    </button>
                  </div>
                );
              })}
              {shown.map((p) =>
                renderItem(
                  itemFor(p.key),
                  { gridColumn: `${p.col + 1} / span ${p.span}`, gridRow: p.level + 2 },
                  p.span,
                  `${p.continuesBefore ? " cont-before" : ""}${p.continuesAfter ? " cont-after" : ""}`
                )
              )}
              {hiddenPerDay.map((n, c) =>
                n > 0 ? (
                  <button
                    key={`more${c}`}
                    className="mv-more"
                    style={{ gridColumn: c + 1, gridRow: maxLevels + 1 }}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      setMore({ day: days[w * 7 + c], anchor: anchorOf(ev.currentTarget.closest(".mv-week")!.children[c]) });
                    }}
                  >
                    Mais {n}
                  </button>
                ) : null
              )}
            </div>
          );
        })}
      </div>

      {more && (
        <Popover anchor={more.anchor} onClose={() => setMore(null)} width={220} style={{ padding: "12px 8px" }}>
          <div className="mv-more-head">
            <div className="tg-dow">{WEEKDAYS_SHORT[weekday(more.day)].toUpperCase()}</div>
            <div style={{ fontSize: 26 }}>{parts(more.day).d}</div>
          </div>
          <div className="mv-more-list">
            {items
              .filter((i) => i.start <= more.day && i.endExclusive > more.day)
              .sort((a, b) => a.order - b.order)
              .map((i) => renderItem(itemFor(i.key), {}))}
          </div>
        </Popover>
      )}
      {holidayPopover.element}
    </div>
  );
}
